import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IRowIdentifier,
  StewartTableColumn,
} from "controls/global/stewart-table/StewartTable";
import { IColumn } from "entities/ApiModel/IColumn";
import {
  getColumnSettings,
  saveColumnSettings,
} from "utils/api/uiSettingsApi";

const DEFAULT_DEBOUNCE_MS = 0;

interface UseColumnSettingsResult<T extends IRowIdentifier> {
  columns: StewartTableColumn<T>[];
  hiddenColumns: (keyof T)[];
  isLoading: boolean;
  onColumnResize: (property: keyof T, newWidth: number) => void;
  onColumnsModified: (next: StewartTableColumn<T>[]) => void;
  setColumnHidden: (field: keyof T, hidden: boolean) => void;
  showAllColumns: () => void;
  hideAllColumns: () => void;
}

function toIColumns<T extends IRowIdentifier>(
  cols: StewartTableColumn<T>[]
): IColumn[] {
  return cols.map((c, idx) => ({
    field: String(c.field),
    name: c.name,
    width: c.width,
    position: c.position ?? idx + 1,
    sortable: c.sortable,
    resizable: c.resizable,
    draggable: c.draggable,
    sticky: c.sticky,
  }));
}

function mergeOverrides<T extends IRowIdentifier>(
  base: StewartTableColumn<T>[],
  overrides: IColumn[]
): StewartTableColumn<T>[] {
  if (!overrides || overrides.length === 0) return base;
  const overrideMap = new Map<string, IColumn>();
  overrides.forEach((o) => overrideMap.set(String(o.field), o));
  return base.map((col) => {
    const o = overrideMap.get(String(col.field));
    if (!o) return col;
    return {
      ...col,
      width: typeof o.width === "number" ? o.width : col.width,
      position: typeof o.position === "number" ? o.position : col.position,
    };
  });
}

function sortByPosition<T extends IRowIdentifier>(
  cols: StewartTableColumn<T>[]
): StewartTableColumn<T>[] {
  return [...cols].sort((a, b) => {
    const ap = a.position ?? Number.MAX_SAFE_INTEGER;
    const bp = b.position ?? Number.MAX_SAFE_INTEGER;
    return ap - bp;
  });
}

function buildHiddenFromOverrides<T extends IRowIdentifier>(
  overrides: IColumn[]
): (keyof T)[] {
  return overrides
    .filter((o) => o.hidden)
    .map((o) => o.field as keyof T);
}

// Construct the partial wire payload mirroring the Inventory pattern:
// one entry per changed field, carrying only the changed property.
function buildPartial(
  fields: string[],
  prop: "width" | "hidden" | "position",
  value: number | boolean
): IColumn[] {
  return fields.map(
    (f) => ({ field: f, name: "", [prop]: value } as IColumn)
  );
}

export function useColumnSettings<T extends IRowIdentifier>(
  gridName: string,
  defaultColumns: StewartTableColumn<T>[],
  protectedFields: (keyof T)[] = [],
  debounceMs: number = DEFAULT_DEBOUNCE_MS
): UseColumnSettingsResult<T> {
  const [userPrefs, setUserPrefs] = useState<IColumn[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const pendingPayloadRef = useRef<IColumn[] | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load saved user prefs on mount / when gridName changes
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getColumnSettings(gridName).then((data) => {
      if (cancelled) return;
      console.debug("[useColumnSettings] load", { gridName, response: data });
      setUserPrefs(Array.isArray(data) ? data : []);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [gridName]);

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      const pending = pendingPayloadRef.current;
      if (pending && pending.length > 0) {
        // Fire and forget; ignore errors on unmount
        saveColumnSettings(gridName, pending).catch(() => undefined);
        pendingPayloadRef.current = null;
      }
    };
  }, [gridName]);

  const scheduleSave = useCallback(
    (payload: IColumn[]) => {
      pendingPayloadRef.current = payload;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        const toSave = pendingPayloadRef.current;
        pendingPayloadRef.current = null;
        debounceTimerRef.current = null;
        if (!toSave) return;
        saveColumnSettings(gridName, toSave).then((saved) => {
          console.debug("[useColumnSettings] save → response", {
            gridName,
            sent: toSave,
            saved,
          });
          // Intentionally do NOT replace local userPrefs with the response.
          // The server echoes the full merged state taken before applying
          // our delta — using it would (1) clobber the user's newly-set
          // value if the server snapshot is stale, and (2) revert any
          // concurrent local edits made while this POST was in flight.
          // Local state stays authoritative; next mount's GET reloads
          // canonical server state.
        });
      }, debounceMs);
    },
    [gridName, debounceMs]
  );

  const onColumnResize = useCallback(
    (property: keyof T, newWidth: number) => {
      const key = String(property);
      setUserPrefs((prev) => {
        const map = new Map<string, IColumn>();
        prev.forEach((c) => map.set(c.field, { ...c }));
        defaultColumns.forEach((c) => {
          const k = String(c.field);
          if (!map.has(k)) {
            map.set(k, {
              field: k,
              name: c.name,
              width: c.width,
              position: c.position,
            });
          }
        });
        const current = map.get(key) || { field: key, name: "" };
        current.width = newWidth;
        map.set(key, current);
        const updated = Array.from(map.values());
        console.debug("[useColumnSettings] resize", {
          gridName,
          property: key,
          newWidth,
          updated,
        });
        return updated;
      });
      // Send only the changed column × changed prop. Mirrors Inventory.
      scheduleSave(buildPartial([key], "width", newWidth));
    },
    [defaultColumns, scheduleSave, gridName]
  );

  const onColumnsModified = useCallback(
    (nextCols: StewartTableColumn<T>[]) => {
      setUserPrefs(toIColumns(nextCols));
      // Reorder: position is the only property that changes per row. Send
      // {field, position} only — no width/hidden riding along.
      const partial: IColumn[] = nextCols.map((c, idx) => ({
        field: String(c.field),
        name: "",
        position: c.position ?? idx + 1,
      }));
      scheduleSave(partial);
    },
    [scheduleSave]
  );

  const setColumnHidden = useCallback(
    (field: keyof T, hidden: boolean) => {
      const protectedSet = new Set(protectedFields.map((f) => String(f)));
      const key = String(field);
      if (protectedSet.has(key) && hidden) return;
      setUserPrefs((prev) => {
        const map = new Map<string, IColumn>();
        prev.forEach((c) => map.set(c.field, { ...c }));
        defaultColumns.forEach((c) => {
          const k = String(c.field);
          if (!map.has(k)) {
            map.set(k, {
              field: k,
              name: c.name,
              width: c.width,
              position: c.position,
            });
          }
        });
        const current = map.get(key) || { field: key, name: "" };
        current.hidden = hidden;
        map.set(key, current);
        return Array.from(map.values());
      });
      scheduleSave(buildPartial([key], "hidden", hidden));
    },
    [defaultColumns, protectedFields, scheduleSave]
  );

  const showAllColumns = useCallback(() => {
    const allFields = defaultColumns.map((c) => String(c.field));
    setUserPrefs((prev) => {
      const map = new Map<string, IColumn>();
      prev.forEach((c) => map.set(c.field, { ...c, hidden: false }));
      defaultColumns.forEach((c) => {
        const k = String(c.field);
        if (!map.has(k)) {
          map.set(k, {
            field: k,
            name: c.name,
            width: c.width,
            position: c.position,
            hidden: false,
          });
        }
      });
      return Array.from(map.values());
    });
    scheduleSave(buildPartial(allFields, "hidden", false));
  }, [defaultColumns, scheduleSave]);

  const hideAllColumns = useCallback(() => {
    const protectedSet = new Set(protectedFields.map((f) => String(f)));
    const fieldsToHide = defaultColumns
      .map((c) => String(c.field))
      .filter((f) => !protectedSet.has(f));
    setUserPrefs((prev) => {
      const map = new Map<string, IColumn>();
      prev.forEach((c) => map.set(c.field, { ...c }));
      defaultColumns.forEach((c) => {
        const k = String(c.field);
        const isProtected = protectedSet.has(k);
        const existing = map.get(k) || {
          field: k,
          name: c.name,
          width: c.width,
          position: c.position,
        };
        existing.hidden = !isProtected;
        map.set(k, existing);
      });
      return Array.from(map.values());
    });
    scheduleSave(buildPartial(fieldsToHide, "hidden", true));
  }, [defaultColumns, protectedFields, scheduleSave]);

  const columns = useMemo(
    () => sortByPosition(mergeOverrides(defaultColumns, userPrefs)),
    [defaultColumns, userPrefs]
  );

  const hiddenColumns = useMemo(
    () => buildHiddenFromOverrides<T>(userPrefs),
    [userPrefs]
  );

  return {
    columns,
    hiddenColumns,
    isLoading,
    onColumnResize,
    onColumnsModified,
    setColumnHidden,
    showAllColumns,
    hideAllColumns,
  };
}

export default useColumnSettings;
